/**
 * @file Reports — the Schedule-E-shaped export as PURE data (T2.11).
 *
 * ## What this module is
 *
 * One read-only projection over rows that already exist, turned into a table a
 * landlord can hand to their accountant. It is not a table, not a migration, and
 * not a report engine: `rent_charges`, counted `ledger_entries` of type `payment`,
 * and `expense_entries` are read, given the dates that mean something for each of
 * them, and written out in one long format.
 *
 * The projection lives HERE, in the kernel, because three surfaces must agree
 * about it and a second implementation is how they start disagreeing:
 *
 *   * the CSV document itself (`scheduleECsv`),
 *   * the JSON summary the Reports page renders its real row counts from
 *     (`scheduleEReportSummary`),
 *   * and the tests, which can prove the period semantics and the correction rule
 *     without a database or a request.
 *
 * The API loads rows and calls in; the route writes the bytes. Nothing in this
 * file touches drizzle, a clock or a request.
 *
 * ## There are TWO expense-shaped things in this product, and this export reads ONE
 *
 * `expense_entries` is **property-scoped** — a cost against a building — and it is
 * what the expense rows here come from. `ledger_entries.entry_type = 'expense'` is
 * **lease-attributed** and append-only. They are different questions and different
 * lifecycles (`packages/shared/src/expenses.ts` states the distinction; migration
 * 0010's header states it again). `affectsTenantBalance` keeps the ledger's
 * `expense` type out of every tenant balance, and this export reads no such row:
 * its only ledger rows are `payment` rows.
 *
 * ## The three row kinds, and why they are NOT totalled
 *
 * A row is one of exactly three things, and the `kind` column says which:
 *
 *   * `rent_charge` — an OBLIGATION. What a lease was billed for a period.
 *   * `payment` — money RECORDED as received, a counted ledger row. The
 *     `payment_state` column carries §7.2's lifecycle, because an uncleared check
 *     is not money in the bank.
 *   * `expense` — a COST entered against a building.
 *
 * **There is no total row in the document, and there is no single "total" figure
 * anywhere in it.** Adding an obligation to money received to a cost produces a
 * number about nothing: charged rent is not money, received rent is money, and a
 * cost is money out. A reader totals the rows they mean, which is a decision only
 * they can make. That is why the CSV's `amount` column is a non-negative
 * magnitude with no sign convention to misread.
 *
 * ## Period semantics, stated rather than implied
 *
 * A range is INCLUSIVE of both ends and is applied to the date field that is
 * meaningful for each row type — never to one field borrowed across all three:
 *
 *   * a `rent_charge` by its **`due_date`** — the day the obligation falls due on
 *     the building's calendar (the T1.4 rule). `period_start`/`period_end` are the
 *     period the money pays for, which is in the description, not the filter: a
 *     landlord and an accountant both reason about the day a payment is due.
 *   * a `payment` by the **calendar day it was recorded**, read in the BUILDING's
 *     timezone (`localDateInZone`, the same rule every other money date follows),
 *     so a payment entered at 9pm in Detroit does not land on tomorrow.
 *   * an `expense` by its **`date`** — the day the cost was incurred, which is what
 *     a landlord typed.
 *
 * `REPORT_PERIOD_SEMANTICS` is that paragraph as one sentence, and it travels in
 * the CSV's own preamble and in the JSON payload so no screen has to remember to
 * add it.
 *
 * ## Which payment rows count
 *
 * `countedLedgerEntries` (`./ledger.js`) decides, and this file does not
 * re-derive it: a row counts iff it is live and no live correction points at it.
 * The API hands over the WHOLE ledger, unfiltered by type, because a correction of
 * a payment is routinely an `adjustment` and filtering the query would drop the
 * correction and leave the superseded original counting.
 *
 * ## Attributing a payment to a building
 *
 * A payment's building is reached through its LEASE (`ledger → lease → unit →
 * property`), which is a column on every ledger row and therefore always
 * answerable. This is deliberately NOT the P&L's stricter rule
 * (`apps/api/src/routes/expenses.ts`), which attributes money to a building only
 * through the charge the payment names — a different question ("which obligation
 * did this money settle") asked by a different surface. Here the question is
 * "which building did this money move for", and the description says, per row,
 * whether it was applied to a charge at all. The P&L's `unattributed` figure and
 * this export therefore do not have to be the same number, and neither is wrong.
 *
 * ## What the export is NOT
 *
 * A reporting convenience, not a tax document. No depreciation is computed, no
 * deductibility is decided, no personal/business split is made, no prior-year
 * figure is carried, and nothing here is a filed return. `REPORT_BASIS_REASON` is
 * that statement, and it is written into the CSV's preamble and returned in the
 * JSON so no surface can present the file as more than it is.
 */
import { centsToDollarString } from "./constants.js";
import { serializeCsv } from "./csv.js";
import {
  EXPENSE_CATEGORY_LABELS,
  type ExpenseCategory,
} from "./expenses.js";
import {
  countedLedgerEntries,
  type LedgerEntryLike,
  type LedgerPaymentState,
} from "./ledger.js";
import { RENT_CHARGE_STATUS_LABELS, type RentChargeStatus } from "./rent-charges.js";

// ---------------------------------------------------------------------------
// The words the file and the page both use
// ---------------------------------------------------------------------------

/**
 * The download's filename, as a **safe literal**.
 *
 * Deliberately a constant with no interpolation: the workspace name, the property
 * label and every query value are caller-controlled, and a newline in a
 * `Content-Disposition` header splits the response. The filters are recorded in
 * the file's own preamble instead, where they belong. The SPA imports this same
 * constant, so the browser's suggestion and the server's header cannot drift.
 */
export const SCHEDULE_E_CSV_FILENAME = "neighboros-schedule-e-export.csv";

/**
 * What the export is, in one sentence, for the CSV preamble and the page.
 *
 * A rendered set of money columns invites a person to read the file as a return.
 * It is not one: it is a convenience built from the rows that exist, and saying so
 * is the difference between a useful export and a plausible one.
 */
export const REPORT_BASIS_REASON =
  "a reporting convenience built from the rows recorded in this portfolio — NOT a " +
  "tax document and not a filed return. The platform computes no depreciation, " +
  "decides no deductibility, makes no personal/business split, knows nothing about " +
  "a period before the records began, and carries no prior-year figure. The rows " +
  "are not totalled: an obligation, money received and a cost are different " +
  "claims, and adding them would be a number about nothing.";

/**
 * Which date field each row type is placed by, in one sentence.
 *
 * The sentence is data rather than a comment because it must reach the person
 * reading the file and the page: a range that silently meant three different
 * things would produce a wrong answer that looks right.
 */
export const REPORT_PERIOD_SEMANTICS =
  "Each row is placed by the date that means something for what it is, and a " +
  "range is inclusive of both ends: a rent charge by the day it falls due (its " +
  "due date, on the building's calendar), a payment by the calendar day it was " +
  "recorded in the building's timezone, and an expense by the date the cost was " +
  "incurred.";

// ---------------------------------------------------------------------------
// The columns
// ---------------------------------------------------------------------------

/**
 * The row kinds, exactly the three source nouns.
 *
 * `payment` is named for the ledger's own `entry_type = 'payment'` rather than
 * "money received", because a row may be `pending` or `returned` — the
 * `payment_state` column is what says whether it is money in the bank, and a kind
 * that pre-empted that would be a claim the row cannot support.
 */
export const SCHEDULE_E_ROW_KINDS = ["rent_charge", "payment", "expense"] as const;
export type ScheduleERowKind = (typeof SCHEDULE_E_ROW_KINDS)[number];

/**
 * The order the kinds appear in on one date.
 *
 * Obligations first, then the money that moved, then the costs: the order a
 * landlord reads a month in. It is also the tiebreak that makes the file's row
 * order TOTAL, so two exports of the same data are byte-identical.
 */
export const SCHEDULE_E_KIND_ORDER: Readonly<Record<ScheduleERowKind, number>> = {
  rent_charge: 0,
  payment: 1,
  expense: 2,
};

/**
 * The column order, exactly as it appears in the header row and in the file.
 *
 * `schedule_e_line` carries the IRS Schedule E Part I expense line a cost was
 * filed under — the `label` from `EXPENSE_CATEGORIES`, the form's own wording, NOT
 * a second list. An income row leaves it blank: the fourteen lines are EXPENSE
 * lines, and writing "rents received" into one of them would be inventing an
 * income taxonomy the shared list does not own.
 *
 * `amount` is dollars with two decimals, produced by `centsToDollarString` at the
 * final formatting step and never before: the arithmetic is integer cents
 * (CLAUDE.md / INV-MONEY-1).
 */
export const SCHEDULE_E_COLUMNS = [
  { key: "property", header: "property" },
  { key: "unit", header: "unit" },
  { key: "kind", header: "kind" },
  { key: "schedule_e_line", header: "schedule_e_line" },
  { key: "date", header: "date" },
  { key: "description", header: "description" },
  { key: "amount", header: "amount" },
  { key: "payment_state", header: "payment_state" },
  { key: "reference", header: "reference" },
] as const;

export type ScheduleEColumnKey = (typeof SCHEDULE_E_COLUMNS)[number]["key"];

// ---------------------------------------------------------------------------
// The facts the projection reads
// ---------------------------------------------------------------------------

/** The three property columns a row's display name is built from. */
export interface ScheduleEPropertyRef {
  readonly label: string | null;
  readonly addressLine1: string;
  readonly city: string;
  readonly state: string;
}

/**
 * How a building is named in one cell.
 *
 * The SAME shape the Expenses screen renders (`propertyLabel ?? addressLine1`,
 * then city and state): a label is a nickname a landlord chose and the address is
 * what identifies the building, so both appear, and two differently-named
 * buildings cannot collapse into one cell.
 */
export function scheduleEPropertyName(property: ScheduleEPropertyRef): string {
  const name = property.label ?? property.addressLine1;
  return [name, property.city, property.state].filter((part) => part.length > 0).join(" · ");
}

/** One `rent_charges` row, with the context that names where it belongs. */
export interface ScheduleEChargeFact {
  readonly id: string;
  /** The display name of the building the lease is in. */
  readonly property: string;
  readonly unitLabel: string;
  /** The day the obligation falls due, `YYYY-MM-DD`. The row's filter date. */
  readonly dueDate: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  /** Non-negative integer cents. */
  readonly amountCents: number;
  readonly status: RentChargeStatus;
}

/**
 * One ledger `payment` row, with the context that names where it belongs.
 *
 * Extends `LedgerEntryLike`, so `countedLedgerEntries` — the ONE balance rule —
 * can be applied to it directly rather than to a re-shaped copy.
 */
export interface ScheduleEPaymentFact extends LedgerEntryLike {
  readonly property: string;
  readonly unitLabel: string;
  /**
   * The local calendar day the payment was recorded, read in the BUILDING's
   * timezone. The row's filter date.
   */
  readonly date: string;
  readonly rentChargeId: string | null;
  readonly paymentState: LedgerPaymentState;
  readonly memo: string | null;
  /** The instrument reference — a check number, a money-order number. */
  readonly reference: string | null;
  /**
   * `periodStart..periodEnd` of the charge this payment names, when it names one.
   *
   * `null` means the money was recorded without being applied to any charge. That
   * is stated in the row's description rather than dropped or divided up: it is
   * real money in the portfolio that answers no obligation question.
   */
  readonly appliedPeriod: string | null;
}

/** One `expense_entries` row, with the building it is a cost against. */
export interface ScheduleEExpenseFact {
  readonly id: string;
  readonly property: string;
  readonly category: ExpenseCategory;
  /** Non-negative integer cents. */
  readonly amountCents: number;
  /** The day the cost was incurred, `YYYY-MM-DD`. The row's filter date. */
  readonly date: string;
  readonly memo: string | null;
}

/** Everything the projection reads. Plain facts — no rows, no live objects. */
export interface ScheduleEExportFacts {
  readonly workspaceName: string;
  /** The property filter, or `null` for the whole portfolio. */
  readonly propertyId: string | null;
  /** The filtered building's display name, when one is filtered. */
  readonly propertyLabel: string | null;
  /** Inclusive lower bound, `YYYY-MM-DD`, or `null` for "from the beginning". */
  readonly from: string | null;
  /** Inclusive upper bound, `YYYY-MM-DD`, or `null` for "to the end". */
  readonly to: string | null;
  readonly charges: readonly ScheduleEChargeFact[];
  /**
   * EVERY ledger row in scope, unfiltered by type — `countedLedgerEntries` needs
   * the whole correction chain in hand to decide which rows may be counted.
   */
  readonly payments: readonly ScheduleEPaymentFact[];
  readonly expenses: readonly ScheduleEExpenseFact[];
}

// ---------------------------------------------------------------------------
// The period filter
// ---------------------------------------------------------------------------

/**
 * Is this row's own date inside the range?
 *
 * `YYYY-MM-DD` strings, compared lexicographically — which is chronological for
 * that format and needs no parsing, no `Date`, and no timezone to get wrong. An
 * absent bound means "no bound", and both bounds are INCLUSIVE.
 */
export function inScheduleEPeriod(
  date: string,
  from: string | null,
  to: string | null,
): boolean {
  if (from !== null && date < from) return false;
  if (to !== null && date > to) return false;
  return true;
}

// ---------------------------------------------------------------------------
// The rows
// ---------------------------------------------------------------------------

/** One projected row. `amountCents` is a MAGNITUDE — never a signed posting. */
export interface ScheduleEReportRow {
  /** The source row's id. Not emitted; it makes the row order total. */
  readonly id: string;
  readonly kind: ScheduleERowKind;
  readonly property: string;
  readonly unit: string;
  /** The Schedule E Part I line for an expense; empty for every income row. */
  readonly scheduleELine: string;
  readonly date: string;
  readonly description: string;
  /** Non-negative integer cents. */
  readonly amountCents: number;
  readonly paymentState: LedgerPaymentState | null;
  readonly reference: string | null;
}

/**
 * A money magnitude, or a bug.
 *
 * A charge and an expense are unsigned by their own columns, so a negative one is
 * a data fault that would silently become a positive dollar figure if it were
 * `Math.abs`ed. A payment is signed by the ledger's convention (negative = money
 * received), so its magnitude is exactly what it means here. Integer cents
 * throughout; the division happens once, in `scheduleERowCells`.
 */
function magnitudeCents(kind: ScheduleERowKind, amountCents: number): number {
  if (!Number.isInteger(amountCents)) {
    throw new Error(
      `reports: ${String(amountCents)} is not a whole number of cents — money is ` +
        "INTEGER CENTS (CLAUDE.md)",
    );
  }
  if (kind === "payment") return Math.abs(amountCents);
  if (amountCents < 0) {
    throw new Error(
      `reports: a ${kind} amount is unsigned and must not be negative ` +
        `(${String(amountCents)} cents)`,
    );
  }
  return amountCents;
}

/**
 * The charge's own sentence: the period it pays for and where §6.1 says it is.
 *
 * The status is rendered through the shared label map, so the file and the Rent &
 * charges page use the same words for the same state.
 */
function chargeDescription(charge: ScheduleEChargeFact): string {
  return (
    `Rent charge ${charge.periodStart} to ${charge.periodEnd} ` +
    `(${RENT_CHARGE_STATUS_LABELS[charge.status]})`
  );
}

/**
 * The payment's sentence.
 *
 * Whether the money was applied to a charge is stated, because it is the one fact
 * a reader cannot recover from the other columns — and a payment that names no
 * charge is exactly the record that would otherwise look like every other one.
 */
function paymentDescription(payment: ScheduleEPaymentFact): string {
  const applied =
    payment.appliedPeriod === null
      ? "not applied to a rent charge"
      : `applied to ${payment.appliedPeriod}`;
  const memo = payment.memo === null ? "" : ` — ${payment.memo}`;
  return `Payment recorded (${applied})${memo}`;
}

/** The expense's sentence: the memo a person typed, or the line they filed under. */
function expenseDescription(expense: ScheduleEExpenseFact): string {
  return expense.memo ?? EXPENSE_CATEGORY_LABELS[expense.category];
}

/** The rows that fall in the period, in a TOTAL and stable order. */
function projectRows(facts: ScheduleEExportFacts): readonly ScheduleEReportRow[] {
  const rows: ScheduleEReportRow[] = [];

  for (const charge of facts.charges) {
    if (!inScheduleEPeriod(charge.dueDate, facts.from, facts.to)) continue;
    rows.push({
      id: charge.id,
      kind: "rent_charge",
      property: charge.property,
      unit: charge.unitLabel,
      // The fourteen lines are EXPENSE lines: an income row asserts no line.
      scheduleELine: "",
      date: charge.dueDate,
      description: chargeDescription(charge),
      amountCents: magnitudeCents("rent_charge", charge.amountCents),
      paymentState: null,
      reference: null,
    });
  }

  // The ONE balance rule decides which ledger rows may be counted; this file must
  // not filter ledger rows for money a second way.
  for (const payment of countedLedgerEntries(facts.payments)) {
    if (payment.entryType !== "payment") continue;
    if (!inScheduleEPeriod(payment.date, facts.from, facts.to)) continue;
    rows.push({
      id: payment.id,
      kind: "payment",
      property: payment.property,
      unit: payment.unitLabel,
      scheduleELine: "",
      date: payment.date,
      description: paymentDescription(payment),
      amountCents: magnitudeCents("payment", payment.amountCents),
      paymentState: payment.paymentState,
      reference: payment.reference,
    });
  }

  for (const expense of facts.expenses) {
    if (!inScheduleEPeriod(expense.date, facts.from, facts.to)) continue;
    rows.push({
      id: expense.id,
      kind: "expense",
      property: expense.property,
      // A cost hangs off the BUILDING, not a tenancy, so it names no unit.
      unit: "",
      scheduleELine: EXPENSE_CATEGORY_LABELS[expense.category],
      date: expense.date,
      description: expenseDescription(expense),
      amountCents: magnitudeCents("expense", expense.amountCents),
      paymentState: null,
      reference: null,
    });
  }

  return rows.sort(compareRows);
}

/**
 * The file's order: date first, then the kind order, then the building, then the
 * source id.
 *
 * Every comparison ends in the id, so the ordering is TOTAL — two exports of the
 * same rows are byte-identical, and a test can assert an exact document rather
 * than a set of lines.
 */
function compareRows(a: ScheduleEReportRow, b: ScheduleEReportRow): number {
  if (a.date !== b.date) return a.date < b.date ? -1 : 1;
  const kindDifference = SCHEDULE_E_KIND_ORDER[a.kind] - SCHEDULE_E_KIND_ORDER[b.kind];
  if (kindDifference !== 0) return kindDifference;
  if (a.property !== b.property) return a.property < b.property ? -1 : 1;
  if (a.id === b.id) return 0;
  return a.id < b.id ? -1 : 1;
}

/** One row's cells, in `SCHEDULE_E_COLUMNS` order. */
export function scheduleERowCells(row: ScheduleEReportRow): readonly string[] {
  return [
    row.property,
    row.unit,
    row.kind,
    row.scheduleELine,
    row.date,
    row.description,
    // The ONE place cents become dollars, and the only division in this module.
    centsToDollarString(row.amountCents),
    row.paymentState ?? "",
    row.reference ?? "",
  ];
}

/** The counts behind the rows, each one folded from the rows themselves. */
export interface ScheduleEReportCounts {
  readonly rows: number;
  readonly rentCharges: number;
  readonly payments: number;
  readonly expenses: number;
  /** Distinct buildings among the rows — not the portfolio's building count. */
  readonly properties: number;
}

/** The projection: the rows, the counts, and the sentences that qualify them. */
export interface ScheduleEReport {
  readonly from: string | null;
  readonly to: string | null;
  readonly propertyId: string | null;
  readonly propertyLabel: string | null;
  readonly counts: ScheduleEReportCounts;
  readonly rows: readonly ScheduleEReportRow[];
  readonly periodSemantics: string;
  readonly basis: string;
}

/**
 * **The projection**, as one pure function.
 *
 * `counts` is folded from the rows this returns, so the page's "12 rows" and the
 * file's 12 data records are the same claim rather than two calculations.
 */
export function buildScheduleEReport(facts: ScheduleEExportFacts): ScheduleEReport {
  const rows = projectRows(facts);
  return {
    from: facts.from,
    to: facts.to,
    propertyId: facts.propertyId,
    propertyLabel: facts.propertyLabel,
    counts: {
      rows: rows.length,
      rentCharges: rows.filter((row) => row.kind === "rent_charge").length,
      payments: rows.filter((row) => row.kind === "payment").length,
      expenses: rows.filter((row) => row.kind === "expense").length,
      properties: new Set(rows.map((row) => row.property)).size,
    },
    rows,
    periodSemantics: REPORT_PERIOD_SEMANTICS,
    basis: REPORT_BASIS_REASON,
  };
}

// ---------------------------------------------------------------------------
// The document
// ---------------------------------------------------------------------------

/**
 * The document's leading `key,value` rows.
 *
 * They are the honest place for the things a column cannot say: which portfolio
 * and building the file covers, which dates were asked for, which date field each
 * row type uses, what `kind` and `schedule_e_line` mean, how a formula-looking cell
 * is guarded, and what the file is NOT. Every value is free text and therefore
 * passes the serializer's formula guard like any other cell.
 */
function preambleRows(
  facts: ScheduleEExportFacts,
  report: ScheduleEReport,
): readonly (readonly string[])[] {
  return [
    ["report", "Schedule E-shaped export of the rows recorded in this portfolio"],
    ["portfolio", facts.workspaceName],
    [
      "property",
      facts.propertyId === null ? "All properties" : (facts.propertyLabel ?? "One property"),
    ],
    ["from", facts.from ?? "beginning of recorded history"],
    ["to", facts.to ?? "end of recorded history"],
    ["rows_exported", String(report.counts.rows)],
    ["period_dates", REPORT_PERIOD_SEMANTICS],
    [
      "row_kinds",
      "rent_charge = a rent obligation; payment = a ledger row recording money, " +
        "see payment_state; expense = a cost entered against a building",
    ],
    [
      "schedule_e_line",
      "the IRS Schedule E Part I expense line the cost was filed under; blank on " +
        "income rows",
    ],
    [
      "formula_guard",
      "a cell beginning with =, +, -, @, tab or carriage return is prefixed with " +
        "an apostrophe so a spreadsheet treats it as text",
    ],
    ["basis", REPORT_BASIS_REASON],
  ];
}

/**
 * **The CSV document**, ready to write to an HTTP body.
 *
 * Layout: the preamble rows, one blank row, the column header row, then one row
 * per fact. With no rows in the period the document still contains the preamble
 * and the header — a valid CSV with headers and no data records, never an error
 * and never a fabricated zero row.
 */
export function scheduleECsv(facts: ScheduleEExportFacts): string {
  const report = buildScheduleEReport(facts);
  const rows: (readonly string[])[] = [
    ...preambleRows(facts, report),
    [],
    SCHEDULE_E_COLUMNS.map((column) => column.header),
    ...report.rows.map(scheduleERowCells),
  ];
  return serializeCsv(rows);
}

// ---------------------------------------------------------------------------
// The wire shape
// ---------------------------------------------------------------------------

/**
 * GET /workspaces/:workspaceId/reports/schedule-e — the same projection as JSON.
 *
 * Deliberately NOT a second implementation: the counts come from
 * `buildScheduleEReport`, the same function the CSV is built from, so the page's
 * "this export would contain 12 rows" cannot disagree with the file's 12 records.
 *
 * It carries no money figure. The page's job is to describe the export and to
 * offer it; a dollar total on screen would be the one thing this feature must not
 * invent.
 */
export interface ScheduleEReportSummaryDto {
  readonly from: string | null;
  readonly to: string | null;
  readonly propertyId: string | null;
  readonly propertyLabel: string | null;
  readonly counts: ScheduleEReportCounts;
  readonly columns: readonly { readonly key: ScheduleEColumnKey; readonly header: string }[];
  readonly periodSemantics: string;
  readonly basis: string;
  /** The safe literal the download is named with, shared with the CSV route. */
  readonly filename: string;
}

/** Project a report into the JSON summary the Reports page renders. */
export function scheduleEReportSummary(report: ScheduleEReport): ScheduleEReportSummaryDto {
  return {
    from: report.from,
    to: report.to,
    propertyId: report.propertyId,
    propertyLabel: report.propertyLabel,
    counts: report.counts,
    columns: SCHEDULE_E_COLUMNS.map((column) => ({
      key: column.key,
      header: column.header,
    })),
    periodSemantics: report.periodSemantics,
    basis: report.basis,
    filename: SCHEDULE_E_CSV_FILENAME,
  };
}
